import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { UserPlus, Loader2, Shield, User } from "lucide-react";

const field = "w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2 text-sm text-[#f7f3ec] outline-none transition-colors placeholder:text-neutral-600 focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]";
const lbl = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400";

export default function Team() {
  const [members, setMembers] = useState(null);
  const [show, setShow] = useState(false);
  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <div className="border-b border-white/10 bg-[#141414]/70 px-8 py-6 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div><h1 className="font-heading text-3xl font-bold tracking-tight text-[#f7f3ec]">Team</h1><p className="mt-1 text-sm text-neutral-500">Members with tracker access. Rates are used for internal cost tracking.</p></div>
          <button onClick={() => setShow(true)} data-testid="add-member-btn" className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90"><UserPlus className="h-4 w-4" strokeWidth={2.5} /> Add Member</button>
        </div>
      </div>
      <div className="p-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {members === null && <div className="col-span-full flex justify-center py-16 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          {members?.map((m) => (
            <div key={m.id} data-testid={`member-${m.id}`} className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5 transition-colors hover:border-[#c5a24a]/40">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#c5a24a]/15 font-heading text-base font-bold text-[#c5a24a]">{(m.name || m.email)[0]?.toUpperCase()}</div>
                <div className="min-w-0"><div className="truncate font-semibold text-[#f7f3ec]">{m.name}</div><div className="truncate text-xs text-neutral-500">{m.email}</div></div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                  {m.role === "admin" ? <><Shield className="h-3.5 w-3.5 text-[#c5a24a]" /><span className="text-[#c5a24a]">Admin</span></> : <><User className="h-3.5 w-3.5 text-neutral-500" /><span className="text-neutral-500">{m.role || "Member"}</span></>}
                </div>
                {m.rate ? <span className="text-xs font-semibold tabular-nums text-neutral-400">${m.rate}/hr</span> : null}
              </div>
              {m.phone && <div className="mt-1 text-xs text-neutral-500">{m.phone}</div>}
            </div>
          ))}
        </div>
      </div>
      {show && <AddMemberModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </Layout>
  );
}

function AddMemberModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "", phone: "", rate: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/team", { ...f, rate: parseFloat(f.rate) || 0 }); toast.success("Member added"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#1a1a1a] shadow-2xl" data-testid="add-member-modal">
        <div className="border-b border-white/10 px-6 py-4"><h3 className="font-heading text-xl font-bold text-[#f7f3ec]">Add Team Member</h3></div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Full Name</label><input data-testid="member-name-input" required className={field} value={f.name} onChange={set("name")} /></div>
            <div><label className={lbl}>Role</label><input className={field} value={f.role} onChange={set("role")} placeholder="Senior Drafter" /></div>
          </div>
          <div><label className={lbl}>Email</label><input data-testid="member-email-input" type="email" required className={field} value={f.email} onChange={set("email")} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Phone</label><input className={field} value={f.phone} onChange={set("phone")} /></div>
            <div><label className={lbl}>Rate ($/hr)</label><input type="number" className={field} value={f.rate} onChange={set("rate")} /></div>
          </div>
          <div><label className={lbl}>Password</label><input data-testid="member-password-input" type="password" required minLength={6} className={field} value={f.password} onChange={set("password")} /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/5">Cancel</button>
            <button data-testid="member-save-btn" disabled={saving} type="submit" className="gold-gradient flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Add</button>
          </div>
        </form>
      </div>
    </div>
  );
}
